/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { DeviceModel } from '../../../core/device';
import { BrandingUiInfoKeys } from '../../../core/entities';
import { ServicesClientImpl } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import { NetworkManager } from '../../../core/network/NetworkManager';
import type { TivoPayloadAction } from '../../../core/tivo/tivo-redux-wrapper';
import { BrandingUiUtility, ListTypes } from '../../../core/utils';
import { WhisperTypes } from '../../../views/Whisper/WhisperTypes';
import {
    onKeyDownList,
    onKeyUpList,
    onMouseOutList,
    onMouseOverList,
} from '../../list/list.reducers';
import {
    showGuideScreen,
    showOnePassManagerScreen,
    showPreviousScreen,
    showRecordingActivityScreen,
    showToDoListScreen,
} from '../../screens/screens.reducers';
import { showWhisper } from '../../whisper/whisper.reducer';
import {
    IManageSelectedOption,
    setManageOptionsListAction,
    setSelectedManageOption,
} from './manage.actions';
import { SET_MANAGE_OPTIONS_LIST, SET_MANAGE_SELECTED_OPTION } from './manage.types';

interface IManage {
    optionsList: Array<IManageSelectedOption>;
    selectedOption: IManageSelectedOption | undefined;
}

const initialState: IManage = {
    optionsList: [],
    selectedOption: undefined,
};

const manage = (state = initialState, { type, payload } = <TivoPayloadAction<IManage>>{}) => {
    switch (type) {
        case SET_MANAGE_OPTIONS_LIST:
            return {
                ...state,
                optionsList: payload.optionsList,
                selectedOption: state.selectedOption ?? payload.optionsList[0],
            };
        case SET_MANAGE_SELECTED_OPTION:
            return {
                ...state,
                selectedOption: payload.selectedOption,
            };

        default:
            return state;
    }
};

export const setManageOptionsList = () => (dispatch: Function) => {
    const settingsItemList: Array<IManageSelectedOption> = [
        {
            title: Locale.getString(LocaleKeys.ONEPASS_MANAGER_TITLE),
            preview: Locale.getString(LocaleKeys.SETTINGS_ONEPASS_PREVIEW_TEXT),
            isDisabled: false,
        },
        {
            title: Locale.getString(LocaleKeys.TODO_LIST_TITLE),
            preview: Locale.getString(LocaleKeys.TODO_LIST_PREVIEW_TEXT),
            isDisabled: false,
        },
    ];

    settingsItemList.push({
        title: Locale.getString(LocaleKeys.RECORDING_ACTIVITY_TITLE),
        preview: Locale.getString(LocaleKeys.RECORDING_ACTIVITY_PREVIEW_TEXT),
        isDisabled: false,
    });

    dispatch(setManageOptionsListAction(settingsItemList));
};

/**
 * Below reducers are related to operations on key events.
 */

export const onKeyBackManage = () => (dispatch: Function) => {
    dispatch(showPreviousScreen());
};

export const onKeyDownManage = () => (dispatch: Function, getState: Function) => {
    const {
        list: {
            [ListTypes.MANAGE]: { position },
        },
        manage: { optionsList },
    } = getState();
    if (position < optionsList.length - 1) {
        dispatch(setSelectedManageOption(optionsList[position + 1]));
        dispatch(onKeyDownList(ListTypes.MANAGE));
    }
};

export const onKeyUpManage = () => (dispatch: Function, getState: Function) => {
    const {
        list: {
            [ListTypes.MANAGE]: { position },
        },
        manage: { optionsList },
    } = getState();
    if (position > 0) {
        dispatch(setSelectedManageOption(optionsList[position - 1]));
        dispatch(onKeyUpList(ListTypes.MANAGE));
    }
};

export const onKeyGuideManage = () => (dispatch: Function) => {
    dispatch(showGuideScreen());
};

export const onKeyOkManage = () => (dispatch: Function, getState: Function) => {
    if (NetworkManager.isNetworkDisconnected()) {
        const service = new ServicesClientImpl();

        const whisperMessage = BrandingUiUtility.formatDescription(
            Locale.getString(LocaleKeys.NO_NETWORK_WHISPER_DESCRIPTION),
            {
                DEVICE_NAME_SHORT: service.getBrandingUiInfoForKey(
                    BrandingUiInfoKeys.DEVICE_NAME_SHORT,
                ),
                BUTTON_NAME_SELECT: DeviceModel.isWebOsPlatform()
                    ? Locale.getString(LocaleKeys.BUTTON_NAME_SELECT_LG)
                    : Locale.getString(LocaleKeys.BUTTON_NAME_SELECT_SAMSUNG),
            },
        );
        dispatch(showWhisper({ message: whisperMessage, iconType: WhisperTypes.NO_ICON }));
        return;
    }

    const {
        list: {
            [ListTypes.MANAGE]: { position },
        },
        manage: { optionsList },
    } = getState();

    const selectedItem = optionsList[position].title;

    switch (selectedItem) {
        case Locale.getString(LocaleKeys.ONEPASS_MANAGER_TITLE):
            dispatch(showOnePassManagerScreen());
            break;
        case Locale.getString(LocaleKeys.TODO_LIST_TITLE):
            dispatch(showToDoListScreen());
            break;
        case Locale.getString(LocaleKeys.RECORDING_ACTIVITY_TITLE):
            dispatch(showRecordingActivityScreen());
            break;
        default:
    }
};

export const onMouseClickManage = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;
    if (value.includes(ListTypes.MANAGE)) dispatch(onKeyOkManage());
};

export const onMouseOutManage = () => (dispatch: Function) => {
    dispatch(onMouseOutList());
};

export const onMouseOverManage = () => (dispatch: Function, getState: Function) => {
    dispatch(onMouseOverList());
    const {
        list: {
            [ListTypes.MANAGE]: { position },
        },
        manage: { optionsList },
    } = getState();
    dispatch(setSelectedManageOption(optionsList[position]));
};

export default manage;
